import orgDefaultImg from 'assets/img/v2/icon/icon_org_default.png';

import Button from 'components/Button';
import ModalContainer from 'components/ModalContainer';
import Icon from 'components/Icon';

import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from 'react-i18next';
import { uiActions, selectShowModalSpinner } from "stores/slice/ui";

import storeService from 'services/Auth.Store.service';
import { cloneDeep, has, size, pick } from 'lodash';

import { organizationType } from 'const/nuclias/organization';

const OrgTreePreviewModal = ({
  modalStatus,
  handleModals
}) => {

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const showModalSpinner = useSelector(selectShowModalSpinner);
  const { pageContext: { selectedOrg } } = useSelector((store) => store);
  const [modelInfo, setModelInfo] = useState({});

  const init = async () => {
    try {
      dispatch(uiActions.showModalSpinner());
      await initModalData()
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(uiActions.hideModalSpinner());
    }
  }

  const initModalData = async () => {
    const editObj = modalStatus.preview.editObj
    let defaultInfo = {
      parentPage: 'treeView',
      orgType: editObj.orgType,
      orgId: selectedOrg.id,
      actions: editObj.actions
    }
    if (editObj.orgType === organizationType.org) {
      const pickOrg = pick(selectedOrg, ['iconS3Path', 'id', 'name', 'owner', 'phone', 'email'])
      defaultInfo.title = 'c1ca926603'
      defaultInfo = { ...defaultInfo, ...pickOrg }
    } else if (editObj.orgType === organizationType.site) {
      const res = await storeService.getSite(editObj.id)
      const pickList = ['address', 'id', 'name', 'storeConfig.settings', 'parentStoreTag', 'owner', 'phone', 'email', 'devices']
      const pickSite = pick(res.data, pickList)
      defaultInfo.title = 'a7d6475ec8'
      defaultInfo = { ...defaultInfo, ...pickSite }
    } else {
      defaultInfo.title = '3039a00fca'
      defaultInfo.id = editObj.id
      defaultInfo.name = editObj.name
    }
    setModelInfo(defaultInfo)
  }

  const contactTable = () => {
    const list = [
      { label: '49ee308734', value: modelInfo.owner },
      { label: 'bcc254b55c', value: modelInfo.phone },
      { label: 'ce8ae9da5b', value: modelInfo.email },
    ]
    return (
      <tbody>
        <tr>
          <td colSpan="3"><br /><strong>{t('7b30c410c4')}</strong></td>
        </tr>
        {list.map((item, index) => {
          return (
            <tr key={`contact_${index}`}>
              <td style={{ minWidth: '50px' }}>{t(item.label)}</td>
              <td>:</td>
              <td className='ps-2'>{item.value}</td>
            </tr>
          )
        })}
      </tbody>
    )
  }

  const orgTable = () => {
    return (
      <>
        <div className='mb-3'>
          <img
            style={{ maxWidth: '100px', maxHeight: '60px', cursor: 'pointer' }}
            src={modelInfo.iconS3Path || orgDefaultImg}
            alt=''
          />
        </div>
        <table>{contactTable()}</table>
      </>
    )
  }

  const siteTable = () => {
    const list = [
      { label: 'dd7bf230fd', value: modelInfo.address?.address },
      { label: '59716c9749', value: modelInfo.address?.country },
      { label: '3039a00fca', value: modelInfo.parentStoreTag?.name },
      { label: '45c10605e0', value: modelInfo.address?.fullTimezone },
      { label: '94ecb80600', value: modelInfo.storeConfig?.settings[0]?.NTPServer1 },
      { label: '4a85af30a7', value: modelInfo.storeConfig?.settings[0]?.NTPServer2 },
    ]

    return (
      <table>
        <tbody>
          {list.map((item, index) => {
            return (
              <tr key={`site_${index}`}>
                <td style={{ minWidth: '50px' }}>{t(item.label)}</td>
                <td>:</td>
                <td className='ps-2'>{item.value}</td>
              </tr>
            )
          })}
        </tbody>
        {contactTable()}
      </table>
    )
  }

  const getTable = () => {
    if (modelInfo.orgType === organizationType.org) return orgTable()
    else if (modelInfo.orgType === organizationType.site) return siteTable()
    else return (<></>)
  }

  const closeModal = () => {
    handleModals({ modal: 'preview', status: false, type: null })
  }

  useEffect(() => {
    if (modalStatus?.preview?.status) init()
  }, [modalStatus?.preview?.status]);

  return (
    <ModalContainer
      modalWidthType='modal-500px'
      openModal={modalStatus.preview.status}
      hideClose={true}
    >
      <div className='header d-flex justify-content-between'>
        <div className='title'>{`${t(modelInfo?.title)} - ${modelInfo?.name}`}</div>
        <div className='d-flex' style={{ gap: '5px' }}>
          {modelInfo?.orgType === organizationType.org &&
            <>
              <Icon
                style={{ width: '19px', height: '19px' }}
                className="icon-site"
                disabled={!modelInfo?.actions?.createSite}
                onClick={() => {
                  if (!modelInfo?.actions?.createSite) return
                  closeModal()
                  handleModals({ modal: 'site', status: true, type: 'create', editObj: { orgId: selectedOrg.id } })
                }}
              />
              <Icon
                style={{ width: '19px', height: '19px' }}
                className="icon-tag"
                disabled={!modelInfo?.actions?.createTag}
                onClick={() => {
                  if (!modelInfo?.actions?.createTag) return
                  closeModal()
                  handleModals({ modal: 'tag', status: true, type: 'create', editObj: { orgId: selectedOrg.id } })
                }}
              />
            </>
          }
          <Icon
            style={{ width: '19px', height: '19px' }}
            className="icon-edit"
            disabled={!modelInfo?.actions?.canEdit}
            onClick={() => {
              if (!modelInfo?.actions?.canEdit) return
              closeModal()
              const modal = modelInfo.orgType === organizationType.org ? 'org' :
                (modelInfo.orgType === organizationType.tag ? 'tag' : 'site')
              handleModals({ modal, status: true, type: 'edit', editObj: modelInfo })
            }}
          />
          <Icon
            style={{ width: '19px', height: '19px' }}
            className="icon-trash"
            disabled={!modelInfo?.actions?.canDelete}
            onClick={() => {
              if (!modelInfo?.actions?.canDelete) return
              closeModal()
              handleModals({ modal: 'delete', status: true, type: null, editObj: modelInfo })
            }}
          />
        </div>
      </div>
      <div className='body'>
        <div>{size(modelInfo) > 0 && getTable()}</div>
      </div>
      <div className={`footer ${modelInfo.orgType === organizationType.tag ? 'non-border-top-footer' : ''}`}>
        <Button
          label='Close'
          className='btn-cancel'
          onClick={() => closeModal()}
        />
      </div>
    </ModalContainer>
  );
};

export default OrgTreePreviewModal;
